import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { cn } from 'src/utils/cn';

interface BaseModalProps {
  readonly children: React.ReactNode;
  readonly open: boolean;
  readonly onClose: (value: boolean) => void;
  readonly className?: string;
  readonly backdropClassName?: string;
  readonly panelClassName?: string;
  readonly size?:
    | 'sm'
    | 'md'
    | 'lg'
    | 'xl'
    | '2xl'
    | '3xl'
    | '4xl'
    | '5xl'
    | '6xl'
    | '7xl'
    | '8xl'
    | '9xl'
    | '10xl';
}

/**
 * Base modal component that provides common modal functionality
 * Used as foundation for other modal components to reduce duplication
 */
export function BaseModal({
  children,
  open,
  onClose,
  className = '',
  backdropClassName = '',
  panelClassName = '',
  size = 'sm',
}: BaseModalProps) {
  const sizeClasses = {
    sm: 'sm:max-w-sm',
    md: 'sm:max-w-md',
    lg: 'sm:max-w-lg',
    xl: 'sm:max-w-xl',
    '2xl': 'sm:max-w-2xl',
    '3xl': 'sm:max-w-3xl',
    '4xl': 'sm:max-w-4xl',
    '5xl': 'sm:max-w-5xl',
    '6xl': 'sm:max-w-6xl',
    '7xl': 'sm:max-w-7xl',
    '8xl': 'sm:max-w-8/10',
    '9xl': 'sm:max-w-9/10',
    '10xl': 'sm:max-w-10/10',
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className={cn('relative z-[1000]', className)}
    >
      <DialogBackdrop
        transition
        className={cn(
          'fixed inset-0 bg-gray-500/75 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in',
          backdropClassName,
        )}
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className={cn(
              'relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in sm:my-8 sm:w-full sm:p-6 data-closed:sm:translate-y-0 data-closed:sm:scale-95',
              sizeClasses[size],
              panelClassName,
            )}
          >
            {children}
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}

export default BaseModal;
